import { getSvgIconName, Icon, ICONS, SvgIcon } from '@bob/library'
import { Asset, AssetProps } from '@websrc/App/Components/Asset/Asset'
import { CTA } from '@websrc/App/Components/CTA'
import { mapSelectorItem } from '@websrc/DataMappers/mapSelector'
import clsx from 'clsx'
import { box, gridArea, gridAreaTemplate, styles, themeVars, typography } from 'leds'
import React, { useState } from 'react'

export interface SelectorListItemProps {
  content: ReturnType<typeof mapSelectorItem>
  showImageColumn: boolean
  showGradeBand: boolean
  dataTestId?: string
}

export const SelectorListItem: React.FC<SelectorListItemProps> = ({
  content: { image, cta, icon, title },
  showImageColumn,
  showGradeBand,
  dataTestId,
}) => {
  const [showEnlargedImage, setShowEnlargedImage] = useState(false)

  const imageSize = 64
  const enlargedImageSize = 128

  const assetProps: AssetProps = {
    fit: 'bounds',
    // Fetch image in only one size to use in all cases
    sizes: `${enlargedImageSize}px`,
    srcSetWidths: [100, 200, 400],
    thumbnail: image ?? undefined,
  }

  return (
    <div
      className={clsx(
        box({
          background: 'white',
          border: 'topBottom',
          borderColor: 'grey4',
        }),
        gridAreaTemplate({
          template: showGradeBand ? 'listSelectorContent' : 'listSelectorContentNoGradeband',
        }),
        styles({
          paddingX: 16,
          paddingY: 8,
          display: 'flex',
          flexDirection: 'row',
          width: 'full',
        })
      )}
      data-testid={dataTestId}
    >
      {showImageColumn && (
        <div
          className={clsx(
            gridArea({ area: 'image' }),
            styles({ width: imageSize, maxWidth: imageSize, marginRight: 16, position: 'relative' })
          )}
          onMouseEnter={() => setShowEnlargedImage(true)}
          onMouseLeave={() => setShowEnlargedImage(false)}
        >
          {image && (
            <>
              {/* Image that is shown when hovering over the image int he selector */}
              <div
                className={styles({
                  width: enlargedImageSize,
                  maxWidth: enlargedImageSize,
                  position: 'absolute',
                  zIndex: 3,
                  // position the center of the enlarged image in the center of the selector image
                  left: 'half',
                  transform: 'moveHalfLeft',
                })}
                hidden={!showEnlargedImage}
              >
                <div
                  className={box({
                    border: 'full',
                    borderColor: 'grey5',
                    rounded: 'small',
                    background: 'white',
                    content: 'center',
                  })}
                >
                  <Asset
                    {...assetProps}
                    className={styles({ margin: 2 })}
                    dataTestId={`image-hover-${image.title}`}
                  />
                </div>
              </div>

              {/* Image shown in the selector. */}
              <Asset dataTestId={`image-${image.title}`} {...assetProps} />
            </>
          )}
        </div>
      )}

      <div className={gridArea({ area: 'title' })}>
        <p className={typography({ size: 'md' })}>{title}</p>
      </div>

      {showGradeBand && (
        <div className={gridArea({ area: 'label' })}>
          <p className={typography({ size: 'sm' })}>
            {icon.icon && (
              <SvgIcon
                className={styles({ marginRight: 5 })}
                color={'black'}
                name={getSvgIconName(icon.icon)}
                size={13}
                title={icon.label || title}
              />
            )}
            {icon.label}
          </p>
        </div>
      )}

      <div className={clsx(gridArea({ area: 'cta' }), styles({ maxWidth: 110 }))}>
        <CTA
          cta={cta}
          prefix={
            <Icon
              color={themeVars.color.black}
              icon={{ ...ICONS.DOWNLOAD, ...(cta && { title: cta.text }) }}
              size={10}
            />
          }
          type={'icon'}
        />
      </div>
    </div>
  )
}
